import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useI18n } from '@locales/client';

import { useCurrencyAddress } from '@/entities/currency/model/hooks';
import { StyledTooltip } from '@/entities/cashback/ui/styles';

import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
  BodyTextBoldTertiary,
  BodyTextRegularPrimary,
  BodyTextRegularSecondary,
  BodyTextRegularTertiary,
  BoldHeadingQuinary,
} from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box';
import { SkeletonLoader } from '@/shared/ui/skeleton';
import CopyIcon from '@/shared/assets/copy-icon.svg';
import CheckIcon from '@/shared/assets/check.svg';
import GiftWallet from '@/shared/assets/gift-wallet.svg';
import { Condition } from '@/shared/ui/condition';
import Switch from '@/shared/ui/switch/switch';
import BonusInfoBlack from '@/shared/assets/bonus-info-black.svg';
import { formatCurrency } from '@/shared/libs/format-currency';
import { useUser } from '@/shared/providers/user-provider/use-user';

import { DepositButton } from '../fiat-form/styles';
import {
  AddressText,
  BonusInfo,
  BottomDescriptions,
  CopyButton,
  DepistAddressContent,
  DepositAddressBonus,
  DepositAddressBonusWrapper,
  DepositAddressWrapper,
  QrContainer,
  QrWrapper,
} from './styles';

const DepositAddress = ({
  currentCurrency,
  setBonusActive,
  bonusActive,
  bonus,
}) => {
  const {
    currencyAddress,
    currencyAddressLoading,
    currencyAddressError,
    setBonusTrigger,
  } = useCurrencyAddress({ code: currentCurrency.code, bonus: bonus?.id });
  const { user } = useUser();
  const t = useI18n();
  // CopyButton
  const [copyState, setCopyState] = useState(false);
  const currentText = t(`wallet.deposit.${copyState ? 'copied' : 'copy'}`);
  const currentIcon = copyState ? <CheckIcon /> : <CopyIcon />;
  const variant = copyState ? 'orangeGradient' : 'gray';
  //

  useEffect(() => {
    if (currencyAddress?.bonus?.isActive) {
      setBonusActive(currencyAddress?.bonus?.isActive);
    }
  }, [currencyAddress?.bonus?.isActive]);

  useEffect(() => {
    if (copyState) {
      // Возвращаем в исходное состояние через 2 секунды
      setTimeout(() => {
        setCopyState((prev) => !prev);
      }, 2000);
    }
  }, [copyState]);

  const copyToClipboard = () => async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCheckBonus = async (checked) => {
    try {
      setBonusActive(checked);
      await setBonusTrigger({
        bonusID: checked ? currencyAddress.bonus.id : 0,
        orderID: currencyAddress.paymentID,
      });
    } catch (e) {}
  };
  if (currencyAddressLoading && !currencyAddressError) {
    return (
      <DepositAddressWrapper>
        <BoldHeadingQuinary>
          {t('wallet.deposit.title')} {currentCurrency.name}
        </BoldHeadingQuinary>
        <Box $paddingTop={8} $paddingBottom={16}>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.deposit.desc', { token: currentCurrency.name })}
          </BodyTextRegularSecondary>
        </Box>
        <QrContainer>
          <SkeletonLoader width={235} height={270} />
        </QrContainer>
        <BottomDescriptions>
          <SkeletonLoader width={150} height={40} />

          <Box
            $display="flex"
            $gap={4}
            $justifyContent="center"
            $marginTop={16}
          >
            <SkeletonLoader width={235} height={20} />
          </Box>
        </BottomDescriptions>
      </DepositAddressWrapper>
    );
  }
  return (
    <DepositAddressWrapper>
      <BoldHeadingQuinary>
        {t('wallet.deposit.title')} {currentCurrency.name}
      </BoldHeadingQuinary>
      <Box $paddingTop={8} $paddingBottom={16}>
        <BodyTextRegularSecondary color="#AFAFAF">
          {t('wallet.deposit.desc', { token: currentCurrency.name })}
        </BodyTextRegularSecondary>
      </Box>

      <QrContainer>
        <QrWrapper>
          <BonusInfo
            data-tooltip-id="bonus-info"
            data-tooltip-content={t('wallet.deposit.onetime_tooltip')}
          >
            <BodyTextBoldTertiary color="#161639">
              {t('wallet.deposit.onetime')}
            </BodyTextBoldTertiary>
            <BonusInfoBlack />
          </BonusInfo>
          <StyledTooltip id="bonus-info" />

          <QRCodeSVG
            bgColor={'#edeff5'}
            imageSettings={{
              src: currentCurrency.logo,
              x: undefined,
              y: undefined,
              height: 40,
              width: 40,
              excavate: true,
            }}
            value={currencyAddress?.payAddress}
            size={200}
          />
          <BodyTextRegularTertiary color="#161639">
            <AddressText>{currencyAddress?.payAddress}</AddressText>
          </BodyTextRegularTertiary>
        </QrWrapper>
      </QrContainer>
      <BottomDescriptions>
        <CopyButton
          type="button"
          onClick={copyToClipboard(currencyAddress?.payAddress)}
          icon={currentIcon}
          variant={variant}
          disabled={copyState}
        >
          <BodyTextBoldSecondary color="#ffff">
            {currentText}
          </BodyTextBoldSecondary>
        </CopyButton>
        <Box $display="flex" $gap={4} $justifyContent="center" $marginTop={16}>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.deposit.min')}
          </BodyTextRegularSecondary>
          <BodyTextBoldSecondary color="#EDEFF5">
            {new Intl.NumberFormat('he-IL', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(currentCurrency.minPayment)}{' '}
            {currentCurrency.code}
          </BodyTextBoldSecondary>
        </Box>
      </BottomDescriptions>
      <Condition match={currencyAddress?.bonus}>
        <DepositAddressBonusWrapper $active={bonusActive}>
          <DepositAddressBonus $active={bonusActive}
          >
            <Box>
              <GiftWallet />
            </Box>

            <DepositButton $active={bonusActive}>
              <Box
                $display="flex"
                $justifyContent={'center'}
                $gap={6}
                $alignItems={'center'}
              >
                <BodyTextRegularPrimary
                  style={{
                    display: 'flex',
                  }}
                >
                    {t('wallet.bonus.percent')}
                  {/* <Condition
                    match={currencyAddress?.bonus?.type === 'freespin'}
                  >
                    + {currencyAddress?.bonus?.spins} FS
                  </Condition>
                  <Condition
                    match={currencyAddress?.bonus?.type !== 'freespin'}
                  >
                    {t('wallet.bonus.title', {
                      bonus: (
                        <BodyTextBoldPrimary color="#FFD60A">
                          {currencyAddress?.bonus?.percent}%
                        </BodyTextBoldPrimary>
                      ),
                    })}
                  </Condition> */}
                </BodyTextRegularPrimary>
              </Box>
            </DepositButton>
            <Condition match={!bonusActive}>
              {t('wallet.bonus.bonus_enable')}
            </Condition>
            <Box $position={'relative'} $zIndex={'1'}>
              <Switch
                onCheckedChange={(e) => handleCheckBonus(e)}
                checked={bonusActive}
              />
            </Box>
          </DepositAddressBonus>
          
          <Condition match={currencyAddress?.bonus?.percent}>
            <DepistAddressContent>
              <BodyTextRegularSecondary color="#AEB8D1">
                {t('wallet.bonus.percent')}
              </BodyTextRegularSecondary>
              <BodyTextBoldSecondary>
                {currencyAddress?.bonus?.percent}%
              </BodyTextBoldSecondary>
            </DepistAddressContent>
          </Condition>
          <Condition match={currencyAddress?.bonus?.spins}>
            <DepistAddressContent>
              <BodyTextRegularSecondary color="#AEB8D1">
                {t('bonus.card.spins')}
              </BodyTextRegularSecondary>
              <BodyTextBoldSecondary>
                {currencyAddress?.bonus?.spins}
              </BodyTextBoldSecondary>
            </DepistAddressContent>
          </Condition>
          <Condition match={currencyAddress?.bonus?.maxDeposit}>
            <DepistAddressContent>
              <BodyTextRegularSecondary color="#AEB8D1">
                {t('wallet.bonus.max')}
              </BodyTextRegularSecondary>
              <BodyTextBoldSecondary
                style={{
                  color:
                    currencyAddress?.bonus?.amount >
                    currencyAddress?.bonus?.maxDeposit
                      ? '#DA4444'
                      : '#AEB8D1',
                }}
              >
                {formatCurrency(
                  currencyAddress?.bonus?.maxDeposit,
                  {
                    currency: user.currency,
                    currencySymbol: user.currencySymbol,
                  },
                  {
                    maximumFractionDigits: 0,
                  }
                )}
              </BodyTextBoldSecondary>
            </DepistAddressContent>
          </Condition>
        </DepositAddressBonusWrapper>
      </Condition>
    </DepositAddressWrapper>
  );
};

export { DepositAddress };
